import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Check, KeyRound, LogOut, MonitorSmartphone, UserCog } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Page, PageHeader } from '@/components/shell'
import { api, ApiError, type AuthConfig } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import { roleLabel } from '../dashboard'

export const Route = createFileRoute('/dashboard/settings')({
  component: SettingsPage,
})

type Profile = {
  id: string
  name: string
  username: string | null
  email: string | null
  role: string
  larkBound: boolean
  hasPassword: boolean
  lastLoginAt: string | null
  createdAt: string
}

type MySession = {
  id: string
  ipAddress: string | null
  userAgent: string | null
  createdAt: string
  expiresAt: string
  current: boolean
}

/**
 * 个人设置。
 *
 * 与「角色管理」页的边界很清楚：那里配置角色与成员，这里只管自己，且改不了角色与状态——
 * 混在一起就等于给所有人开了提权入口。
 */
function SettingsPage() {
  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<Profile>('/profile'),
  })

  return (
    <Page>
      <PageHeader title="个人设置" description="管理你的账号信息、密码与登录设备。" />

      {isLoading || !profile ? (
        <div className="space-y-3.5">
          <Skeleton className="h-40 w-full rounded-apple-lg" />
          <Skeleton className="h-52 w-full rounded-apple-lg" />
        </div>
      ) : (
        <div className="grid gap-3.5 lg:grid-cols-2">
          <ProfileCard profile={profile} />
          <PasswordCard hasPassword={profile.hasPassword} />
          <div className="lg:col-span-2">
            <DevicesCard />
          </div>
        </div>
      )}
    </Page>
  )
}

function SectionCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof UserCog
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="surface p-6">
      <div className="mb-5 flex items-start gap-3">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Icon className="size-4 text-mut" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">{title}</h2>
          {description && (
            <p className="mt-1 text-[12px] leading-relaxed text-mut2">{description}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  )
}

function ProfileCard({ profile }: { profile: Profile }) {
  const qc = useQueryClient()
  const [name, setName] = useState(profile.name)

  const save = useMutation({
    mutationFn: () => api.patch('/profile', { name: name.trim() }),
    onSuccess: () => {
      toast.success('资料已更新')
      qc.invalidateQueries({ queryKey: ['profile'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e) =>
      toast.error('更新失败', { description: e instanceof ApiError ? e.message : '请稍后重试' }),
  })

  const dirty = name.trim() !== profile.name && name.trim().length > 0

  return (
    <SectionCard icon={UserCog} title="基本资料">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-[12.5px]">姓名</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-9 rounded-apple text-[13.5px]"
          />
        </div>

        <dl className="surface-inset space-y-2 p-3.5 text-[12.5px]">
          <Row label="账号" value={profile.username ?? '—'} mono />
          <Row label="邮箱" value={profile.email ?? '未设置'} />
          <Row label="角色" value={roleLabel(profile.role)} />
          <Row label="Lark" value={profile.larkBound ? '已绑定' : '未绑定'} />
          <Row label="最后登录" value={formatDateTime(profile.lastLoginAt)} mono />
        </dl>

        {/* 角色只读——改角色是 admin 的权限，不能从个人设置里走 */}
        <p className="text-[11px] leading-relaxed text-mut2">
          账号名与角色不可自行修改。如需调整，请联系超级管理员。
        </p>

        <Button
          className="pressable h-9 rounded-apple text-[13px]"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? '保存中…' : '保存'}
        </Button>
      </div>
    </SectionCard>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-mut2">{label}</dt>
      <dd className={`text-txt ${mono ? 'font-mono text-[11.5px]' : ''}`}>{value}</dd>
    </div>
  )
}

function PasswordCard({ hasPassword }: { hasPassword: boolean }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  /**
   * 最小长度由服务端下发，不在前端写死。
   *
   * 策略按环境不同（开发 6 位 / 生产 12 位），前端硬编码一个数字的话，
   * 两边必然有一边是错的——用户会看到「至少 12 位」却被后端以别的理由拒绝。
   */
  const { data: config } = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<AuthConfig>('/auth-config'),
  })
  const minLength = config?.minPasswordLength ?? 12

  const tooShort = next.length > 0 && next.length < minLength
  const mismatch = confirm.length > 0 && next !== confirm
  const canSubmit = current.length > 0 && next.length >= minLength && next === confirm

  const change = useMutation({
    mutationFn: () =>
      api.post('/profile/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast.success('密码已更新', { description: '其它设备的登录已同时失效' })
      setCurrent('')
      setNext('')
      setConfirm('')
    },
    onError: (e) =>
      toast.error('修改失败', { description: e instanceof ApiError ? e.message : '请稍后重试' }),
  })

  return (
    <SectionCard
      icon={KeyRound}
      title="修改密码"
      description="修改后其它设备的登录会立即失效——口令泄露时这是止血手段。"
    >
      {!hasPassword ? (
        <p className="text-[13px] leading-relaxed text-mut">
          当前账号通过 Lark 登录，没有设置密码。如需使用账号密码登录，请联系管理员。
        </p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) change.mutate()
          }}
        >
          <div className="space-y-1.5">
            <Label className="text-[12.5px]">当前密码</Label>
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="h-9 rounded-apple text-[13.5px]"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[12.5px]">
              新密码
              <span className="ml-2 font-mono text-[11px] text-mut2">至少 {minLength} 位</span>
            </Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="h-9 rounded-apple text-[13.5px]"
            />
            {tooShort && (
              <p className="text-[11.5px] text-warn">还差 {minLength - next.length} 位</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-[12.5px]">确认新密码</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-9 rounded-apple text-[13.5px]"
            />
            {mismatch && <p className="text-[11.5px] text-rose">两次输入不一致</p>}
          </div>

          <Button
            type="submit"
            className="pressable h-9 rounded-apple text-[13px]"
            disabled={!canSubmit || change.isPending}
          >
            {change.isPending ? '处理中…' : '更新密码'}
          </Button>
        </form>
      )}
    </SectionCard>
  )
}

function DevicesCard() {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['profile', 'sessions'],
    queryFn: () => api.get<{ data: MySession[] }>('/profile/sessions'),
  })

  const revokeOthers = useMutation({
    mutationFn: () => api.post<{ revoked: number }>('/profile/sessions/revoke-others'),
    onSuccess: (r) => {
      toast.success('已登出其它设备', { description: `吊销了 ${r.revoked} 个会话` })
      qc.invalidateQueries({ queryKey: ['profile', 'sessions'] })
    },
    onError: () => toast.error('操作失败'),
  })

  const others = (data?.data ?? []).filter((s) => !s.current).length

  return (
    <SectionCard
      icon={MonitorSmartphone}
      title="登录设备"
      description="当前账号的有效会话。发现陌生设备时应立即登出其它设备并修改密码。"
    >
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-apple-sm" />
          ))}
        </div>
      ) : (
        <>
          <ul className="divide-y divide-line/40">
            {data?.data.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-txt">{describeUserAgent(s.userAgent)}</span>
                    {s.current && (
                      <Badge className="h-4 gap-1 bg-cyan/15 px-1.5 text-[10px] text-cyan hover:bg-cyan/15">
                        <Check className="size-2.5" />
                        当前设备
                      </Badge>
                    )}
                  </div>
                  <div className="tnum mt-0.5 font-mono text-[11px] text-mut2">
                    {s.ipAddress || '未知 IP'} · 登录于 {formatDateTime(s.createdAt)}
                  </div>
                </div>
                <span className="tnum shrink-0 font-mono text-[10.5px] text-dim">
                  {formatDateTime(s.expiresAt)} 过期
                </span>
              </li>
            ))}
          </ul>

          {others > 0 && (
            <Button
              variant="outline"
              className="pressable mt-4 h-9 gap-1.5 rounded-apple text-[13px]"
              disabled={revokeOthers.isPending}
              onClick={() => revokeOthers.mutate()}
            >
              <LogOut className="size-3.5" />
              登出其它 {others} 台设备
            </Button>
          )}
        </>
      )}
    </SectionCard>
  )
}

/** 把 UA 串压成一句人话。识别不出来就原样截断，不猜。 */
function describeUserAgent(ua: string | null): string {
  if (!ua) return '未知设备'

  const os = /Mac OS X/.test(ua)
    ? 'macOS'
    : /Windows/.test(ua)
      ? 'Windows'
      : /Android/.test(ua)
        ? 'Android'
        : /iPhone|iPad/.test(ua)
          ? 'iOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : null

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : null

  if (os && browser) return `${browser} · ${os}`
  return ua.slice(0, 40)
}
